/**
 * Custom Date/Time Picker Components
 *
 * ✅ แสดงวันที่แบบไทย (วัน เดือน ปี)
 * ✅ แสดงเวลา 24 ชั่วโมง
 * ✅ รองรับ i18n (th, en, lo, km)
 * ✅ UI สวยงาม เหมือนหน้า Lossback
 * ✅ เปลี่ยนภาษาอัตโนมัติตามธงชาติ
 */

import React, { forwardRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { th, enUS, enGB } from 'date-fns/locale';
import { format } from 'date-fns';
import { Calendar, Clock } from 'lucide-react';
import { Input } from './input';
import { useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';

// ✅ Register all supported locales
registerLocale('th', th);
registerLocale('en', enUS);
registerLocale('lo', enGB); // Lao uses English format as fallback
registerLocale('km', enGB); // Khmer uses English format as fallback

// ============================================
// 📅 Thai Date Picker Component
// ============================================

interface ThaiDatePickerProps {
  selected?: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  locale?: string; // Optional override (for backward compatibility)
}

export const ThaiDatePicker: React.FC<ThaiDatePickerProps> = ({
  selected,
  onChange,
  disabled = false,
  placeholder = 'เลือกวันที่',
  minDate,
  maxDate,
  locale: localeOverride
}) => {
  // ✅ Get current language from i18n
  const { i18n } = useTranslation();
  const currentLocale = localeOverride || i18n.language;

  // ✅ Custom Input Component with Calendar Icon
  const CustomInput = forwardRef<HTMLInputElement, any>(({ value, onClick, disabled }, ref) => (
    <div className="relative">
      <Input
        ref={ref}
        value={value}
        onClick={onClick}
        disabled={disabled}
        placeholder={placeholder}
        readOnly
        className="pl-10 cursor-pointer"
      />
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500 hover:text-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Calendar className="w-4 h-4" />
      </button>
    </div>
  ));
  CustomInput.displayName = 'CustomInput';

  // ✅ Get today button text based on language
  const todayButtonText = currentLocale === 'th' ? 'วันนี้' : currentLocale === 'lo' ? 'ມື້ນີ້' : currentLocale === 'km' ? 'ថ្ងៃនេះ' : 'Today';

  return (
    // @ts-expect-error - React DatePicker type compatibility issue
    <DatePicker
      selected={selected}
      onChange={onChange}
      disabled={disabled}
      locale={currentLocale}
      dateFormat="d MMMM yyyy"
      minDate={minDate}
      maxDate={maxDate}
      customInput={<CustomInput />}
      showYearDropdown
      showMonthDropdown
      dropdownMode="select"
      todayButton={todayButtonText}
      calendarStartDay={0} // Sunday
      className="w-full"
      wrapperClassName="w-full"
    />
  );
};

// ============================================
// ⏰ Time Picker 24-Hour Component
// ============================================

interface TimePicker24Props {
  selected?: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const TimePicker24: React.FC<TimePicker24Props> = ({
  selected,
  onChange,
  disabled = false,
  placeholder = 'เลือกเวลา'
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  // ✅ Sync input value with selected date
  React.useEffect(() => {
    if (selected) {
      const hours = selected.getHours().toString().padStart(2, '0');
      const minutes = selected.getMinutes().toString().padStart(2, '0');
      setInputValue(`${hours}:${minutes}`);
    } else {
      setInputValue('');
    }
  }, [selected]);

  // ✅ Handle manual input change (Auto-format: 0236 → 02:36)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const cursorPosition = input.selectionStart || 0;
    let value = input.value;

    // ลบทุกอย่างที่ไม่ใช่ตัวเลข
    const digits = value.replace(/\D/g, '');

    // จำกัดไม่เกิน 4 หลัก (HHmm)
    const limitedDigits = digits.slice(0, 4);

    // Auto-format: แทรก : หลัง digit ที่ 2
    let formatted = limitedDigits;
    if (limitedDigits.length >= 3) {
      formatted = limitedDigits.slice(0, 2) + ':' + limitedDigits.slice(2);
    }

    setInputValue(formatted);

    // ✅ Restore cursor position หลัง format
    setTimeout(() => {
      if (inputRef.current) {
        let newCursorPosition = cursorPosition;

        // ถ้ามี : ถูกแทรกเข้ามา ให้ adjust cursor
        if (formatted.includes(':') && limitedDigits.length >= 3) {
          if (cursorPosition >= 2 && !value.includes(':')) {
            newCursorPosition = cursorPosition + 1;
          }
        }

        // จำกัด cursor ไม่ให้เกิน length
        newCursorPosition = Math.min(newCursorPosition, formatted.length);

        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        inputRef.current.focus();
      }
    }, 0);
  };

  // ✅ Handle blur to validate and parse time
  const handleBlur = () => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (timeRegex.test(inputValue)) {
      const [hours, minutes] = inputValue.split(':').map(Number);
      const newDate = new Date();
      newDate.setHours(hours, minutes, 0, 0);
      onChange(newDate);
    } else if (inputValue === '') {
      onChange(null);
    }
    // Invalid format - revert to previous value
    else if (selected) {
      const hours = selected.getHours().toString().padStart(2, '0');
      const minutes = selected.getMinutes().toString().padStart(2, '0');
      setInputValue(`${hours}:${minutes}`);
    } else {
      setInputValue('');
    }
  };

  // ✅ Custom Input Component with Clock Icon (Now Editable!)
  const CustomInput = forwardRef<HTMLInputElement, any>(({ onClick, disabled }, ref) => {
    // Combine refs (DatePicker ref + our inputRef)
    const combinedRef = React.useCallback((el: HTMLInputElement | null) => {
      // Set DatePicker ref
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
      }
      // Set our internal ref
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
    }, [ref]);

    return (
      <div className="relative">
        <Input
          ref={combinedRef}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          className="pl-10 font-mono"
          maxLength={5}
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
          }}
          disabled={disabled}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Clock className="w-4 h-4" />
        </button>
      </div>
    );
  });
  CustomInput.displayName = 'CustomInput';

  return (
    // @ts-expect-error - React DatePicker type compatibility issue
    <DatePicker
      selected={selected}
      onChange={onChange}
      disabled={disabled}
      showTimeSelect
      showTimeSelectOnly
      timeIntervals={15}
      timeCaption="เวลา"
      dateFormat="HH:mm"
      timeFormat="HH:mm"
      customInput={<CustomInput />}
      className="w-full"
      wrapperClassName="w-full"
    />
  );
};

// ============================================
// 📅⏰ Combined Date Time Picker
// ============================================

interface DateTimePickerProps {
  selected?: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  locale?: string;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  selected,
  onChange,
  disabled = false,
  placeholder = 'เลือกวันที่และเวลา',
  minDate,
  maxDate,
  locale: localeOverride
}) => {
  // ✅ Get current language from i18n
  const { i18n } = useTranslation();
  const currentLocale = localeOverride || i18n.language;

  const CustomInput = forwardRef<HTMLInputElement, any>(({ value, onClick, disabled }, ref) => (
    <div className="relative">
      <Input
        ref={ref}
        value={value}
        onClick={onClick}
        disabled={disabled}
        placeholder={placeholder}
        readOnly
        className="pl-10 cursor-pointer"
      />
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500 hover:text-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Calendar className="w-4 h-4" />
      </button>
    </div>
  ));
  CustomInput.displayName = 'CustomInput';

  // ✅ Get today button text based on language
  const todayButtonText = currentLocale === 'th' ? 'วันนี้' : currentLocale === 'lo' ? 'ມື້ນີ້' : currentLocale === 'km' ? 'ថ្ងៃនេះ' : 'Today';
  const timeCaption = currentLocale === 'th' ? 'เวลา' : currentLocale === 'lo' ? 'ເວລາ' : currentLocale === 'km' ? 'ពេលវេលា' : 'Time';

  return (
    // @ts-expect-error - React DatePicker type compatibility issue
    <DatePicker
      selected={selected}
      onChange={onChange}
      disabled={disabled}
      locale={currentLocale}
      dateFormat="d MMMM yyyy, HH:mm"
      timeFormat="HH:mm"
      minDate={minDate}
      maxDate={maxDate}
      customInput={<CustomInput />}
      showTimeSelect
      timeIntervals={15}
      timeCaption={timeCaption}
      showYearDropdown
      showMonthDropdown
      dropdownMode="select"
      todayButton={todayButtonText}
      calendarStartDay={0}
      className="w-full"
      wrapperClassName="w-full"
    />
  );
};

// ============================================
// 🎨 Helper: Format Date for Display
// ============================================

/**
 * Format date for Thai display
 * @param date - Date object
 * @param locale - Locale string (th, en, etc.)
 * @returns Formatted string
 */
export function formatDateDisplay(date: Date | null | undefined, locale: string = 'th'): string {
  if (!date) return '-';

  try {
    if (locale === 'th') {
      return format(date, 'd MMMM yyyy', { locale: th });
    } else {
      return format(date, 'MMMM d, yyyy', { locale: enUS });
    }
  } catch (error) {
    console.error('[formatDateDisplay] Error:', error);
    return '-';
  }
}

/**
 * Format time for 24-hour display
 * @param date - Date object
 * @returns Formatted string (HH:mm)
 */
export function formatTimeDisplay(date: Date | null | undefined): string {
  if (!date) return '-';

  try {
    return format(date, 'HH:mm');
  } catch (error) {
    console.error('[formatTimeDisplay] Error:', error);
    return '-';
  }
}

/**
 * Format datetime for Thai display
 * @param date - Date object
 * @param locale - Locale string
 * @returns Formatted string
 */
export function formatDateTimeDisplay(date: Date | null | undefined, locale: string = 'th'): string {
  if (!date) return '-';

  try {
    if (locale === 'th') {
      return format(date, 'd MMMM yyyy, HH:mm', { locale: th });
    } else {
      return format(date, 'MMMM d, yyyy, HH:mm', { locale: enUS });
    }
  } catch (error) {
    console.error('[formatDateTimeDisplay] Error:', error);
    return '-';
  }
}
